import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useHookstate } from "@hookstate/core";
import { readerDataStore } from "../../../hookstate/readerDataStore";
import { useReadingDirection } from "./useReadingDirection";
import { usePageLayout } from "./usePageLayout";
import { useP2PSettings } from "./useP2PSettings";
import { useReaderLoaded } from "./useReaderLoaded";
import { PAGE_LAYOUT_TYPES } from "../constants";
import { useReaderHintsSettings } from "./useReaderHintsSettings";
import { useIsOverlayOpen } from "./hookstate/useIsOverlayOpen";
import { useIsInReader } from "../../../hooks/useIsInReader";

const HINT_DISPLAY_DURATION = 4000; // 3 seconds

/**
 * Hook to manage reader hints visibility based on reader state changes
 */
export function useReaderHints() {
  const { showReaderHints: userHintsEnabled } = useReaderHintsSettings();

  const [showHints, setShowHints] = useState(false);
  const [hintsVisible, setHintsVisible] = useState(false);

  const inReader = useIsInReader();
  // Only subscribe to the comic ID to prevent unnecessary re-renders
  const comicId = useHookstate(readerDataStore.comicData).get()?.id;

  const { readingDirection, comicIsVertical } = useReadingDirection();
  const { pageLayout } = usePageLayout();
  const { p2pEnabled } = useP2PSettings();
  const { loaded } = useReaderLoaded();
  const { isOverlayOpen } = useIsOverlayOpen();

  // Track previous state to detect transitions
  const prevInReader = useRef(false);
  const prevComicId = useRef<number | undefined>(undefined);
  const prevReadingMode = useRef<string | undefined>(undefined);
  const hasEverLoaded = useRef(false);

  // Memoize current reading mode to prevent unnecessary recalculations
  const currentReadingMode = useMemo(() => {
    if (p2pEnabled) {
      return `p2p-${readingDirection}`;
    }
    if (comicIsVertical || pageLayout === PAGE_LAYOUT_TYPES.vertical) {
      return "vertical";
    }
    if (pageLayout === PAGE_LAYOUT_TYPES.double) {
      return `double-${readingDirection}`;
    }
    return `single-${readingDirection}`;
  }, [p2pEnabled, comicIsVertical, pageLayout, readingDirection]);

  // Main effect for showing/hiding hints based on reader state changes
  useEffect(() => {
    // Case 1: Entering reader from outside
    const enteringReader = !prevInReader.current && inReader;

    // Case 2: Initial load in reader (refresh case) - only on very first load
    const initialReaderLoad = !hasEverLoaded.current && loaded && inReader;

    // Case 3: Switching to different comic within reader (not just different release)
    const switchingComic =
      inReader &&
      loaded &&
      prevComicId.current !== undefined &&
      comicId !== undefined &&
      prevComicId.current !== comicId;

    // Case 4: Changing reading mode
    const changingReadingMode =
      inReader &&
      loaded &&
      prevReadingMode.current !== undefined &&
      prevReadingMode.current !== currentReadingMode;

    // Show hints if reader is fully loaded and any trigger condition is met
    if (
      userHintsEnabled &&
      loaded &&
      (enteringReader ||
        initialReaderLoad ||
        switchingComic ||
        changingReadingMode)
    ) {
      setShowHints(true);
      setHintsVisible(true);
    }
  }, [inReader, comicId, currentReadingMode, loaded, userHintsEnabled]);

  // Separate effect for updating refs to avoid race conditions
  useEffect(() => {
    if (loaded && inReader) {
      prevInReader.current = !!inReader;
      prevComicId.current = comicId;
      prevReadingMode.current = currentReadingMode;
      hasEverLoaded.current = true;
    } else if (!inReader) {
      // Reset when leaving reader
      prevInReader.current = false;
    }
  }, [loaded, inReader, comicId, currentReadingMode]);

  // Memoize scroll handler to avoid recreating function
  const handleScroll = useCallback(() => {
    setHintsVisible(false);
  }, []);

  // Hide hints on scroll
  useEffect(() => {
    if (!userHintsEnabled || !showHints || !hintsVisible) return;

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [showHints, hintsVisible, handleScroll, userHintsEnabled]);

  // Auto-fade after 3 seconds, but only when overlay is closed
  useEffect(() => {
    if (!userHintsEnabled || !showHints || isOverlayOpen) return;

    const timeout = setTimeout(() => {
      setHintsVisible(false);
    }, HINT_DISPLAY_DURATION);

    return () => clearTimeout(timeout);
  }, [showHints, userHintsEnabled, isOverlayOpen]);

  // Hide hints on user interaction
  const hideHints = useCallback(() => {
    setHintsVisible(false);
  }, []);

  // Reset hints when they fade out completely
  useEffect(() => {
    if (!userHintsEnabled || (!hintsVisible && showHints)) {
      // Small delay to allow fade animation to complete
      const timeout = setTimeout(() => {
        setShowHints(false);
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [hintsVisible, showHints, userHintsEnabled]);

  return {
    showHints: userHintsEnabled && showHints,
    hintsVisible: userHintsEnabled && hintsVisible,
    hideHints,
  };
}
